import dayjs from 'dayjs'
import Link from 'next/link'
import React, { ReactNode, useMemo, useRef } from 'react'
import { IoArrowForward, IoCheckmarkCircle } from 'react-icons/io5'
import { useIntersection, useMedia } from 'react-use'
import styled, { css } from 'styled-components'
import { VerificationStatus } from 'types/graphql'
import { SupertesterIcon } from 'icons/Supertester'
import { resizedImageUrl } from 'utils/imageUrl'
import { media, phoneMaxWidth } from 'utils/media'
import { pluralize } from 'utils/pluralize'
import { Avatar } from 'lib/Avatar'
import { LinkButton } from 'lib/Button/Button'
import { LoadingDots } from 'lib/LoadingDots'
import { Text } from 'lib/Text'
import { TippyPopover } from 'lib/TippyPopover'
import { useUserPreview } from './queries/useUserPreview'

const UserHoverContainer = styled.div<{ $hasBio: boolean }>`
  display: grid;
  padding-bottom: 8px;
  width: 100%;
  grid-gap: 16px;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    'header header'
    ${(props) => props.$hasBio && `'description description'`}
    'location joined-time';

  border-bottom: 1.5px solid var(--black-80);

  ${media.phoneUp} {
    width: 272px;
    margin: 24px;
    margin-bottom: 32px;

    cursor: pointer;
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-area: header;
`

const ImageContainer = styled.div`
  display: flex;
  gap: 4px;
  overflow: hidden;

  img {
    height: 88px;
    width: 88px;
    min-width: 88px;
  }

  ${media.phoneUp} {
    img:nth-child(n + 4) {
      display: none;
    }
  }
`

const DetailsContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  width: 100%;
  color: var(--black-100);

  ${Text}:first-child {
    flex-grow: 1;
  }
`

const Description = styled(Text)`
  grid-area: description;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const Location = styled(Text)`
  grid-area: location;
`

const JoinedTime = styled(Text)<{ $takePlaceOfLocation: boolean }>`
  ${(props) =>
    props.$takePlaceOfLocation
      ? css`
          grid-area: location;
        `
      : css`
          grid-area: joined-time;
          text-align: end;
        `}
`

const SmallSupertesterIcon = styled(SupertesterIcon)`
  width: 16px;
  height: 16px;
`

const StyledAvatar = styled(Avatar)`
  background-color: var(--black-30);
`

const ViewProfileButton = styled(LinkButton)`
  width: 100%;
  margin-top: 16px;
  gap: 8px;

  ${media.phoneUp} {
    display: none;
  }
`

const Wrapper = styled.div`
  display: inline-flex;
`

interface Props {
  username: string
  children: ReactNode
  controls: Parameters<typeof TippyPopover>[0]['controls']
  disabled: boolean
}

export const UserHoverDetails = ({ username, children, controls, disabled }: Props) => {
  const isPhoneDown = useMedia(`(max-width: ${phoneMaxWidth}px)`)
  const childrenWrapperRef = useRef<HTMLDivElement | null>(null)
  const intersection = useIntersection(childrenWrapperRef, {
    threshold: 1
  })

  const { loading, data } = useUserPreview({ variables: { username }, skip: !intersection?.isIntersecting || disabled })

  const content = useMemo(
    () =>
      loading || !data?.user ? (
        <LoadingDots />
      ) : (
        <>
          <Header>
            <ImageContainer>
              <StyledAvatar
                size="x-large"
                user={{ avatarFullPath: data.user.avatarFullPath, username, fullName: data.user?.fullName ?? undefined }}
              />
              {data.user.reviews.items.map((review) => (
                <img key={review.brand.title} src={resizedImageUrl(review.brand.primaryImageFullPath, { width: 160, height: 160 })} alt="" />
              ))}
            </ImageContainer>

            <div>
              <Text as="p" size="m" weight="medium">
                {data.user.fullName}
              </Text>
              <DetailsContainer>
                <Text size="ps" as="p">
                  {pluralize(data.user.reviewCount, 'Review', 'Reviews')}
                </Text>
                {(data.user.isSupertester || data.user.verificationStatus !== VerificationStatus.Pending) && data.user.isSupertester ? (
                  <>
                    <SmallSupertesterIcon />
                    <Text size="ps" as="p">
                      {data.user.isSupertester ? 'Supertester' : 'Not supertester'}
                    </Text>
                  </>
                ) : (
                  <>
                    <IoCheckmarkCircle />
                    <Text size="ps" as="p">
                      Verified identity
                    </Text>
                  </>
                )}
              </DetailsContainer>
            </div>
          </Header>

          {data.user.bio && (
            <Description size="ps" as="p">
              {data.user.bio}
            </Description>
          )}

          <Location size="pxs" as="p">
            {data.user.location}
          </Location>
          <JoinedTime size="pxs" as="p" $takePlaceOfLocation={!data.user.location}>
            {dayjs(data.user.createdAt).format('MMMM YYYY')}
          </JoinedTime>
        </>
      ),
    [username, loading, data]
  )

  return (
    <TippyPopover
      controls={controls}
      placement="right"
      delay={[300, 400]}
      defaultTriggerOnDesktop={!disabled}
      content={
        isPhoneDown ? (
          <>
            <UserHoverContainer $hasBio={!!data?.user?.bio}>{content}</UserHoverContainer>
            <ViewProfileButton color="primary" href={`/${username}`}>
              View profile <IoArrowForward size={16} />
            </ViewProfileButton>
          </>
        ) : (
          <Link href={`/${username}`} passHref prefetch={false}>
            <UserHoverContainer $hasBio={!!data?.user?.bio}>{content}</UserHoverContainer>
          </Link>
        )
      }>
      <Wrapper role="button" ref={childrenWrapperRef}>
        {children}
      </Wrapper>
    </TippyPopover>
  )
}
